/**
 * File: apps/qbench-cp/reactapp/src/shared/hooks/api/misc.js
 *
 * Purpose: Miscellaneous shared React Query hooks and a request-params helper used
 *     across several other API hook files.
 *
 * Scope: Portal logo fetch/display, billing metrics summary, recent-reports listing
 *     with attachments merged in, per-entity report PDF fetch/download, and a generic
 *     pagination/search request-params memo used by list hooks.
 *
 * Tags: customer-portal
 *
 * Overview: `fetchPortalLogo` and `usePortalLogo` retrieve the tenant's logo as a blob.
 *     `useBillingMetrics` fetches billing summary data. `useRecentReports` fetches recent
 *     reports and merges each report's associated order/sample/test entity and attachments
 *     into a flattened shape, gated by an access-control check for the reports route.
 *     `useReportPDFbyId` lazily fetches a report PDF as an array buffer, refetch guarded by
 *     the report-download access policy. `useRequestParams` builds a normalized params object
 *     (page, page_size, search, plus any extra filters) memoized on its inputs, reused by
 *     other list-fetching hooks throughout `shared/hooks/api`.
 *
 * Exports: fetchPortalLogo, useBillingMetrics, useRecentReports, useReportPDFbyId,
 *     useRequestParams, usePortalLogo
 */

import { useMemo } from 'react';
import { useQuery } from 'react-query';
import policies from 'shared/configs/rbac/accessPolicies';
import { EntityType } from 'shared/constants/entityTypes';
import { DEFAULT_PAGE_SIZE } from 'shared/constants/misc';
import { axiosinstance } from 'shared/utils/api';
import useAccessControl from '../misc/useAccessControl';

export function fetchPortalLogo() {
	return axiosinstance
		.get(`/v1/logo`, { responseType: 'blob' })
		.then(res => res.data);
}

export const useBillingMetrics = () => {
	const fetchData = () =>
		axiosinstance.get(`/v1/billing`).then(res => res.data);
	const { data, ...queryResult } = useQuery(
		['metrics', 'billing'],
		fetchData
	);

	return {
		data: data?.data || {},
		...queryResult,
	};
};

export function useRecentReports({ daysDelta }) {
  const baseKey = 'recentReports';
  const baseUrl = '/v1/reports/recent';
  const url =
    typeof daysDelta === 'boolean'
      ? baseUrl
      : `${baseUrl}/${parseInt(daysDelta)}`;

  function mapAttachmentsToReports(data = {}) {
    const { reports = [], attachments = {} } = data;
    return reports.map(({ order, sample, test, ...report }) => ({
      entity: order || sample || test || {},
      entityType: !!order
        ? EntityType.ORDER
        : !!sample
        ? EntityType.SAMPLE
        : EntityType.TEST,
      attachments: attachments[report.id],
      report,
    }));
  }

  const canViewReports = useAccessControl(policies.route.reports);
  const { data = [], ...query } = useQuery(
    [baseKey],
    async () => {
      const res = await axiosinstance.get(url);
      return mapAttachmentsToReports(res.data?.data);
    },
    { enabled: canViewReports }
  );

  return { data, ...query };
}

export const useReportPDFbyId = (type, id) => {
	const fetchData = () =>
		id
			? axiosinstance
					.get(`/v1/${type}/${id}/report/pdf`, {
						responseType: 'arraybuffer',
					})
					.then(res => res.data)
			: null;

	const canViewReports = useAccessControl(policies.ui.reportDownloadBtn);
	const { data, isLoading, error, refetch, isFetching } = useQuery(
		['reportPdf', type, parseInt(id)],
		fetchData,
		{
			refetchOnWindowFocus: false,
			enabled: false,
		}
	);

	return [
    data,
    {
      error,
      isLoading,
      isFetching,
      refetch: () => canViewReports && refetch(),
    },
  ];
};

export function useRequestParams({
  page = 1,
  search = '',
  pageSize: page_size = DEFAULT_PAGE_SIZE,
  ...rest
}) {
  const params = useMemo(
    () => ({
      page,
			page_size,
      ...rest,
      ...(Boolean(search) && { search }),
    }),
    [page, search, page_size, rest]
  );
  return params;
}

export function usePortalLogo() {
	const { data } = useQuery(
		['logo'],
		fetchPortalLogo
	);
	return data;
}